// src/firebase/auditLog.ts
//
// Read-side helpers for the registrationAuditLog top-level collection.
// Writes happen inside the runTransaction in firebase/registrations.ts
// (`updateRegistration`) so the audit entry can never diverge from the
// registration update it describes.
//
// Schema:
//   {
//     registrationId, competitionId, athleteId, athleteName,
//     editorUid, editorEmail, changedBy: "coach" | "admin",
//     action: "updated" | "deleted",   // optional; absent ⇒ "updated"
//     changes: { fieldName: { from, to } },
//     timestamp,
//   }
//
// Required Firestore indexes (composite):
//   - (registrationId asc, timestamp desc) — modal history view
//   - (competitionId  asc, timestamp desc) — admin per-competition view

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";
import { withCache, invalidateCachePrefix } from "../utils/cache";

const COLLECTION = "registrationAuditLog";

const TTL_AUDIT = 60 * 1000; // 1 min — audit reads spike when admins open modals

/** Single mutation event on a registration. */
export interface RegistrationAuditEntry {
  id?: string;
  registrationId: string;
  competitionId: string;
  athleteId: string;
  /** Snapshot of "FirstName LastName" at the time of the change. */
  athleteName: string;
  /** Auth uid of whoever clicked "save". */
  editorUid: string;
  /** Snapshot of editor email at the time of the change. */
  editorEmail: string;
  /** Role of the editor at the time of the change. */
  changedBy: "coach" | "admin";
  /**
   * What happened. "updated" is the default for legacy entries that predate this
   * field; "deleted" is written by deleteRegistration() when an admin removes an
   * athlete from a competition.
   */
  action?: "updated" | "deleted";
  /** Only the fields that actually moved; never empty (no-op edits are skipped at write time). */
  changes: Record<string, { from: unknown; to: unknown }>;
  timestamp: Timestamp;
}

export function invalidateAuditLogCache(): void {
  invalidateCachePrefix("auditLog:");
}

export const getAuditLogByRegistration = async (
  registrationId: string
): Promise<RegistrationAuditEntry[]> =>
  withCache(`auditLog:reg:${registrationId}`, TTL_AUDIT, async () => {
    const q = query(
      collection(db, COLLECTION),
      where("registrationId", "==", registrationId),
      orderBy("timestamp", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as RegistrationAuditEntry));
  });

export const getAuditLogByCompetition = async (
  competitionId: string
): Promise<RegistrationAuditEntry[]> =>
  withCache(`auditLog:comp:${competitionId}`, TTL_AUDIT, async () => {
    const q = query(
      collection(db, COLLECTION),
      where("competitionId", "==", competitionId),
      orderBy("timestamp", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as RegistrationAuditEntry));
  });
